import { CommonDataProps, styled } from '@/utils';
import { Box } from '../Box';
import { Button } from '../Button';
import { DrawerContextValue, useRequiredDrawerContext } from './DrawerContextProvider';

export interface DrawerFooterProps extends CommonDataProps {
  /**
   * React node that contains a delete button. This should be a secondary variant button with text that follows the
   * format of "Delete [entity]". This prop is useful when leveraging existing button components (e.g. `DeleteEntity`
   * in cloud).
   *
   * If you have simple button needs, you can instead use the `deleteButtonText` and `onDeleteButtonClick` props.
   */
  deleteButton?: React.ReactNode;
  /**
   * Use this prop to customize the text of the delete button. This should generally follow the format of
   * "Delete [entity]".
   *
   * If you have a more complex use case, you may instead use the `deleteButton` prop to pass a react node where you
   * manage the button text, onClick, etc. yourself.
   *
   * @default 'Delete'
   */
  deleteButtonText?: string;
  /**
   * Click handler for the delete button. If not provided, the delete button will not be rendered.
   *
   * If you have a more complex use case, you may instead use the `deleteButton` prop to pass a react node where you
   * manage the button text, onClick, etc. yourself.
   */
  onDeleteButtonClick?: () => void;
}

/**
 * Drawer footer. Primarily used for "edit" variant drawers to provide a "Delete" button.
 */
export function DrawerFooter({
  deleteButton,
  onDeleteButtonClick,
  deleteButtonText = 'Delete',
  'data-testid': dataTestId,
  'data-trackid': dataTrackId,
}: DrawerFooterProps) {
  const { variant, size } = useRequiredDrawerContext();

  const hasDeleteButton = Boolean(deleteButton || onDeleteButtonClick);
  const shouldShowDeleteButton = variant === 'edit' && hasDeleteButton;

  // The footer currently only has a delete button. If the footer is not configured to show that
  // button, do not render the footer because it will be empty.
  if (!shouldShowDeleteButton) {
    return null;
  }

  return (
    <DrawerFooterContainer size={size} data-testid={dataTestId} data-trackid={dataTrackId}>
      {shouldShowDeleteButton && (
        <DrawerFooterDeleteButton
          deleteButton={deleteButton}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteButtonText={deleteButtonText}
        />
      )}
    </DrawerFooterContainer>
  );
}

function DrawerFooterDeleteButton({ deleteButton, onDeleteButtonClick, deleteButtonText }: DrawerFooterProps) {
  if (deleteButton) {
    return deleteButton;
  }

  return (
    <Button variant="secondary" onClick={onDeleteButtonClick}>
      {deleteButtonText}
    </Button>
  );
}

const DrawerFooterContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<Pick<DrawerContextValue, 'size'>>(({ theme, size }) => {
  return {
    padding: theme.spacing(2),
    borderTop: size === 'small' ? 'none' : `solid 1px ${theme.palette.border.default}`,
    background: size === 'small' ? theme.palette.background.container : theme.palette.background.canvas,
  };
});
